import { useEffect, useState } from 'react'
import { Menu, Sparkles } from 'lucide-react'
import { Link, Outlet, useLocation } from 'react-router-dom'

import { OnlineBookingShareDialog } from '@/components/booking/OnlineBookingShareDialog'
import { AppSidebar } from '@/components/layout/AppSidebar'
import { BranchSwitcher } from '@/components/layout/BranchSwitcher'
import { OnlineBookingToastHost } from '@/components/layout/OnlineBookingToastHost'
import { HeaderNotificationsPanel } from '@/components/layout/HeaderNotificationsPanel'
import { HeaderSearchDialog } from '@/components/layout/HeaderSearchDialog'
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher'
import { UserMenu } from '@/components/layout/UserMenu'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed'
import { cn } from '@/lib/utils'

export function AppShell() {
  const { locale } = useUiLocale()
  const location = useLocation()
  const isPosRoute = location.pathname === '/pos'
  const isRtl = locale === 'ar'
  const { collapsed, toggleCollapsed } = useSidebarCollapsed()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  return (
    <div
      className={cn(
        'luxury-mesh flex flex-col',
        isPosRoute ? 'pos-route h-dvh max-h-dvh overflow-hidden' : 'min-h-dvh',
      )}
    >
      <header
        className={cn(
          'app-topbar sticky top-0 z-50 shrink-0 border-b border-border/40 bg-background/80 backdrop-blur-xl',
          isPosRoute ? 'px-2 lg:px-3' : 'px-4 lg:px-6',
        )}
      >
        <div
          className={cn(
            'flex h-[var(--app-topbar-height)] items-center gap-2 sm:gap-3',
            isPosRoute ? 'w-full max-w-none' : 'mx-auto max-w-[1600px]',
          )}
        >
          {!isPosRoute ? (
            <button
              type="button"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground lg:hidden"
              aria-label="Open menu"
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="h-5 w-5" strokeWidth={2} />
            </button>
          ) : null}

          <Link
            to="/"
            className="flex shrink-0 items-center gap-2 rounded-2xl transition-opacity hover:opacity-90"
            title="Dashboard"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-[hsl(262_70%_48%)] text-white shadow-sm">
              <Sparkles className="h-4 w-4" strokeWidth={2} aria-hidden />
            </div>
            {!isPosRoute ? (
              <div className="hidden min-w-0 sm:block">
                <p className="text-sm font-semibold leading-none tracking-tight">Lumière</p>
                <p className="mt-0.5 hidden text-[10px] font-medium uppercase tracking-widest text-muted-foreground md:block">
                  Ladies Spa · Dubai
                </p>
              </div>
            ) : (
              <p className="hidden text-sm font-semibold leading-none tracking-tight text-foreground sm:block">
                POS
              </p>
            )}
          </Link>

          <BranchSwitcher />

          {!isPosRoute ? (
            <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-1.5">
              <OnlineBookingShareDialog />
              <LanguageSwitcher className="hidden md:flex" />
              <HeaderSearchDialog />
              <HeaderNotificationsPanel />
              <UserMenu />
            </div>
          ) : (
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <LanguageSwitcher className="hidden sm:flex" />
              <HeaderNotificationsPanel />
              <UserMenu />
            </div>
          )}
        </div>
      </header>

      {!isPosRoute ? (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <AppSidebar
            collapsed={collapsed}
            onToggleCollapsed={toggleCollapsed}
            mobileOpen={mobileNavOpen}
            onMobileClose={() => setMobileNavOpen(false)}
            isRtl={isRtl}
          />
          <main className="min-w-0 flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      ) : (
        <main className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </main>
      )}

      <OnlineBookingToastHost />
    </div>
  )
}
